import { useState, useMemo, useEffect } from "react";
import type { RefundApplicationData, RefundReason } from "@/@types/refund.types";
import { REFUND_REASONS } from "@/@types/refund.types";
import { useGetTicketsQuery } from "@/queries/useGetTicketsQuery";
import { useGetRefundApplicationsQuery } from "@/queries/useGetRefundApplicationsQuery";
import { useCreateRefundApplicationMutation } from "@/mutations/useCreateRefundApplicationMutation";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import type { Ticket } from "@/@types/tickets.types";
import { toast } from "sonner";

const getInitialFormData = (): RefundApplicationData => ({
  ticketId: "",
  reason: "",
  otherReason: "",
  status: "draft",
});

// Helper to get remaining sessions from ticket
const getRemainingSessionsFromTicket = (ticket: Ticket | undefined): number => {
  if (!ticket) return 0;
  if (ticket.type === "package" || ticket.type === "membership") {
    if (!ticket.service_list || ticket.service_list.length === 0) return 0;
    return ticket.service_list.reduce((sum, service) => {
      return sum + (Number(service.count) || 0) + (Number(service.count_reserves) || 0);
    }, 0);
  }
  return Number(ticket.count) || 0;
};

export const useRefundApplication = () => {
  const [formData, setFormData] = useState<RefundApplicationData>(getInitialFormData);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const { authPhone } = useAuth();
  const { selectedClubId } = useClub();
  const { data: tickets = [], isLoading: ticketsLoading } = useGetTicketsQuery();
  const { data: existingApplications = [], isLoading: applicationsLoading } =
    useGetRefundApplicationsQuery();
  const createMutation = useCreateRefundApplicationMutation();

  // Get ticket IDs that already have pending refund applications
  const ticketsWithPendingApplications = useMemo(() => {
    return new Set(
      existingApplications
        .filter((app) => app.state !== "complete")
        .map((app) => app.ticket_id)
        .filter(Boolean)
    );
  }, [existingApplications]);

  // Include both active and not_active tickets for refund
  // not_active tickets are purchased but not yet activated
  const activeTickets = useMemo(
    () => tickets.filter((ticket) => ticket.status === "active" || ticket.status === "not_active"),
    [tickets]
  );

  // Filter out tickets that already have pending applications
  const availableTickets = useMemo(
    () => activeTickets.filter((ticket) => !ticketsWithPendingApplications.has(ticket.ticket_id)),
    [activeTickets, ticketsWithPendingApplications]
  );

  const selectedTicket = useMemo(
    () => availableTickets.find((ticket) => ticket.ticket_id === formData.ticketId),
    [availableTickets, formData.ticketId]
  );

  useEffect(() => {
    if (ticketsLoading || applicationsLoading) return;
    if (!formData.ticketId) return;

    const stillAvailable = availableTickets.some(
      (ticket) => ticket.ticket_id === formData.ticketId
    );
    if (!stillAvailable) {
      setFormData((prev) => ({ ...prev, ticketId: "" }));
    }
  }, [availableTickets, applicationsLoading, formData.ticketId, ticketsLoading]);

  // Helper to get initial sessions from service_list_atm_sale
  const getInitialSessionsFromTicket = (ticket: Ticket | undefined): number => {
    if (!ticket) return 0;
    if (ticket.type === "package" || ticket.type === "membership") {
      if (!ticket.service_list_atm_sale || ticket.service_list_atm_sale.length === 0) return 0;
      return ticket.service_list_atm_sale.reduce((sum, service) => {
        return sum + (Number(service.count) || 0);
      }, 0);
    }
    return 0;
  };

  // Helper to get single session price from onetime_service_cost
  const getSingleSessionPriceFromTicket = (ticket: Ticket | undefined): number | null => {
    if (!ticket) return null;
    if (ticket.type === "package" || ticket.type === "membership") {
      if (ticket.onetime_service_cost && ticket.onetime_service_cost > 0) {
        return ticket.onetime_service_cost;
      }
    }
    return null;
  };

  // Calculate refund amount using ticket's cost and service_list_atm_sale
  const refundCalculation = useMemo(() => {
    if (!selectedTicket) return null;
    if (selectedTicket.type !== "package" && selectedTicket.type !== "membership") return null;

    // Get purchase price directly from ticket
    // TODO: remove hardcoded price after API returns real cost
    let purchasePrice = selectedTicket.cost;
    if (
      selectedTicket.ticket_id === "d1985ba5-65b0-4eb8-be26-bd23be98ae66" &&
      purchasePrice === 0
    ) {
      purchasePrice = 20000;
    }
    if (!purchasePrice || purchasePrice <= 0) return null;

    // Get initial sessions from service_list_atm_sale
    const initialSessions = getInitialSessionsFromTicket(selectedTicket);
    if (initialSessions === 0) return null;

    // Get remaining sessions from ticket
    const remainingSessions = getRemainingSessionsFromTicket(selectedTicket);
    const usedSessions = Math.max(0, initialSessions - remainingSessions);

    // Get single session price from ticket data
    const singleSessionPrice = getSingleSessionPriceFromTicket(selectedTicket);
    if (singleSessionPrice === null) return null;

    // Calculate refund: purchase price - (single session price × used sessions)
    const usedAmount = singleSessionPrice * usedSessions;
    const refundAmount = Math.max(0, purchasePrice - usedAmount);

    return {
      purchasePrice,
      initialSessions,
      remainingSessions,
      usedSessions,
      singleSessionPrice,
      usedAmount,
      refundAmount,
    };
  }, [selectedTicket]);

  const showOtherInput = formData.reason === "Другое";
  const isSubmitted = formData.status === "submitted";
  const isCalculationLoading = ticketsLoading;
  const isSubmitting = createMutation.isPending;

  const handleTicketChange = (ticketId: string) => {
    setFormData((prev) => ({
      ...prev,
      ticketId,
    }));
  };

  const handleReasonChange = (reason: RefundReason) => {
    setFormData((prev) => ({
      ...prev,
      reason,
      otherReason: reason === "Другое" ? prev.otherReason : "",
    }));
  };

  const handleOtherReasonChange = (otherReason: string) => {
    setFormData((prev) => ({
      ...prev,
      otherReason,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.ticketId) {
      return false;
    }

    if (!formData.reason) {
      return false;
    }

    if (formData.reason === "Другое" && !formData.otherReason.trim()) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        phone: authPhone,
        clubId: selectedClubId,
        data: {
          reason: formData.reason,
          other_reason: formData.reason === "Другое" ? formData.otherReason : undefined,
          ticket_id: formData.ticketId,
          ticket_title: selectedTicket?.title,
          purchase_price: refundCalculation?.purchasePrice,
          initial_sessions: refundCalculation?.initialSessions,
          remaining_sessions: refundCalculation?.remainingSessions,
          used_sessions: refundCalculation?.usedSessions,
          refund_amount: refundCalculation?.refundAmount,
        },
      });

      const submittedData: RefundApplicationData = {
        ...formData,
        status: "submitted",
        submittedDate: new Date().toISOString(),
      };

      setFormData(submittedData);
      setSuccessDialogOpen(true);
    } catch {
      toast.error("Ошибка при отправке заявления");
    }
  };

  const handleClearApplication = () => {
    setFormData(getInitialFormData());
  };

  return {
    formData,
    showOtherInput,
    isSubmitted,
    isSubmitting,
    successDialogOpen,
    setSuccessDialogOpen,
    activeTickets: availableTickets,
    selectedTicket,
    ticketsLoading,
    handleTicketChange,
    handleReasonChange,
    handleOtherReasonChange,
    handleSubmit,
    handleClearApplication,
    refundReasons: REFUND_REASONS,
    refundCalculation,
    isCalculationLoading,
    existingApplications,
    applicationsLoading,
  };
};
